import { logger } from '../logger';
import type { SupportedLanguages } from '../../i18n';

interface TranslationRequest {
  text: string;
  sourceLang: SupportedLanguages;
  targetLang: SupportedLanguages;
  priority: number;
  timestamp: number;
  resolve: (value: string) => void;
  reject: (error: Error) => void;
}

export class TranslationQueue {
  private static instance: TranslationQueue;
  private queue: TranslationRequest[] = [];
  private processing: boolean = false;
  private readonly BATCH_SIZE = 50; // Nombre maximum de caractères par lot
  private readonly BATCH_INTERVAL = 1000; // 1 seconde entre les lots
  private readonly MAX_RETRIES = 3;

  private constructor() {
    this.startProcessing();
  }

  public static getInstance(): TranslationQueue {
    if (!TranslationQueue.instance) {
      TranslationQueue.instance = new TranslationQueue();
    }
    return TranslationQueue.instance;
  }

  public async queueTranslation(
    text: string,
    sourceLang: SupportedLanguages,
    targetLang: SupportedLanguages,
    priority: number = 0
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      this.queue.push({
        text,
        sourceLang,
        targetLang,
        priority,
        timestamp: Date.now(),
        resolve,
        reject
      });

      this.sortQueue();
    });
  }

  private sortQueue(): void {
    this.queue.sort((a, b) => {
      if (a.priority !== b.priority) {
        return b.priority - a.priority;
      }
      return a.timestamp - b.timestamp;
    });
  }

  private async startProcessing(): Promise<void> {
    while (true) {
      if (this.queue.length === 0) {
        await new Promise(resolve => setTimeout(resolve, 100));
        continue;
      }

      this.processing = true;
      const batch = this.createBatch();

      try {
        const results = await this.translateBatch(batch);
        
        // Distribuer les résultats
        batch.forEach((request, index) => {
          if (results[index].success) {
            request.resolve(results[index].translation);
          } else {
            request.reject(new Error(results[index].error));
          }
        });

      } catch (error) {
        batch.forEach(request => {
          request.reject(error instanceof Error ? error : new Error(String(error)));
        });
      }

      await new Promise(resolve => setTimeout(resolve, this.BATCH_INTERVAL));
      this.processing = false;
    }
  }

  private createBatch(): TranslationRequest[] {
    let currentSize = 0;
    const batch: TranslationRequest[] = [];

    while (this.queue.length > 0 && currentSize < this.BATCH_SIZE) {
      const request = this.queue[0];
      if (currentSize + request.text.length > this.BATCH_SIZE) {
        break;
      }
      batch.push(this.queue.shift()!);
      currentSize += request.text.length;
    }

    return batch;
  }

  private async translateBatch(
    batch: TranslationRequest[]
  ): Promise<Array<{ success: boolean; translation?: string; error?: string }>> {
    const apiKey = import.meta.env.VITE_GOOGLE_TRANSLATE_API_KEY;
    if (!apiKey) {
      throw new Error('Missing Google Translate API key');
    }

    try {
      const response = await fetch(
        `https://translation.googleapis.com/language/translate/v2?key=${apiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            q: batch.map(req => req.text),
            source: batch[0].sourceLang,
            target: batch[0].targetLang,
            format: 'text'
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Translation API error: ${response.status}`);
      }

      const data = await response.json();
      if (data.error) {
        throw new Error(data.error.message);
      }

      return data.data.translations.map((t: any) => ({
        success: true,
        translation: t.translatedText
      }));

    } catch (error) {
      logger.error('Batch translation failed', {
        category: 'TranslationQueue',
        data: { error }
      });

      return batch.map(() => ({
        success: false,
        error: error instanceof Error ? error.message : 'Translation failed'
      }));
    }
  }

  public getQueueLength(): number {
    return this.queue.length;
  }

  public clearQueue(): void {
    this.queue = [];
  }
}

export const translationQueue = TranslationQueue.getInstance();